import { useEffect, useState } from "react";
import {
  Dropdown,
  makeStyles,
  Option,
  useId,
  Badge,
} from "@fluentui/react-components";

type ComponentProps = {
  options: { label: string; value: string }[];
  selectedDropDownValues: string[];
  setSelectedDropDownValues: (values: string[]) => void;
  placeholder?: string;
  onOpenChange?: (e: any, data: { open: boolean }) => void;
  style?: any;
  select_all?: boolean;
  fixed_width?: boolean;
  maxSelectionLimit?: number;
  allSelectedText?: string;
  multiselect?: boolean;
};

const useStyles = makeStyles({
  root: {
    // width: "fit-content !important",
    maxWidth: "100% !important",
    maxHeight: "35px",
    overflowX: "auto",
  },
  fixed_dropdown_button: {
    minWidth: "150px !important",
    maxWidth: "350px !important",
    maxHeight: "35px !important",
    overflowX: "auto",
  },
  dropdown_button: {
    width: "100% !important",
    maxHeight: "60px !important",
  },
  listbox: {
    maxHeight: "50vh !important",
    overflowY: "auto",
  },
});

const Multiselectsearch = (props: ComponentProps) => {
  const {
    options,
    selectedDropDownValues,
    setSelectedDropDownValues,
    placeholder,
    onOpenChange,
    style,
    select_all = true,
    fixed_width = true,
    maxSelectionLimit,
    multiselect = true,
  } = props;
  const comboId = useId("combo-multi");
  const styles = useStyles();
  const [optionState, setOptionState] = useState(options);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const uniqueOptions = options.filter(
      (option, index, self) =>
        index === self.findIndex((t) => t.value === option.value)
    );
    setOptionState(uniqueOptions);
  }, [options]);

  const uniqueOptionValues = optionState.map((o) => o.value);
  const normalizedSelected = Array.from(
    new Set(
      selectedDropDownValues.filter((v) => uniqueOptionValues.includes(v))
    )
  );

  const isAllSelected =
    uniqueOptionValues.length > 0 &&
    normalizedSelected.length === uniqueOptionValues.length;

  const handleOptionChange = (event: any, option: any) => {
    if (!option) return;

    const val = option.optionValue;

    if (multiselect) {
      let newSelectedValues = [...normalizedSelected];

      if (val === "select-all") {
        if (isAllSelected) {
          newSelectedValues = [];
        } else {
          newSelectedValues =
            maxSelectionLimit && uniqueOptionValues.length > maxSelectionLimit
              ? uniqueOptionValues.slice(0, maxSelectionLimit)
              : [...uniqueOptionValues];
        }
      } else {
        const already = newSelectedValues.includes(val);
        if (already) {
          newSelectedValues = newSelectedValues.filter((v) => v !== val);
        } else if (
          !maxSelectionLimit ||
          newSelectedValues.length < maxSelectionLimit
        ) {
          newSelectedValues.push(val);
        }
      }

      newSelectedValues = Array.from(new Set(newSelectedValues)).filter((v) =>
        uniqueOptionValues.includes(v)
      );
      setSelectedDropDownValues(newSelectedValues);
    } else {
      // Single select mode
      if (uniqueOptionValues.includes(val)) {
        setSelectedDropDownValues([val]);
      }
    }
  };

  const renderSelectedOptionsText = () => {
    if (isAllSelected) {
      return props.allSelectedText || "All";
    }

    const displayed = normalizedSelected.slice(0, 2);
    const remainingCount = Math.max(0, normalizedSelected.length - 2);

    const selectedLabels = displayed.map((value) => {
      const opt = optionState.find((o) => o.value === value);
      return opt ? opt.label : "";
    });

    return remainingCount > 0
      ? `${selectedLabels.join(", ")}, +${remainingCount}`
      : selectedLabels.join(", ");
  };

  return (
    <div className={styles.root}>
      <Dropdown
        listbox={{ className: styles.listbox }}
        className={
          fixed_width ? styles.fixed_dropdown_button : styles.dropdown_button
        }
        aria-labelledby={comboId}
        multiselect={multiselect}
        placeholder={
          selectedDropDownValues.length > 0
            ? renderSelectedOptionsText()
            : placeholder
        }
        selectedOptions={
          multiselect
            ? isAllSelected
              ? ["select-all", ...normalizedSelected]
              : normalizedSelected
            : normalizedSelected.slice(0, 1) // for single select
        }
        onOptionSelect={handleOptionChange}
        onOpenChange={(e, data) => {
          setIsDropdownOpen(data.open);
          if (onOpenChange) {
            onOpenChange(e, data); // Call the provided function if it exists
          }
        }}
      >
        {select_all && (
          <Option value="select-all" key="option-select-all" text="Select All">
            Select All
          </Option>
        )}
        {optionState.map((option, index) => (
          <Option
            value={option.value}
            key={`option-${index}`}
            text={option.label}
            disabled={
              maxSelectionLimit &&
              !selectedDropDownValues.includes(option.value) &&
              selectedDropDownValues.length >= maxSelectionLimit
                ? true
                : false
            }
          >
            {option.label}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
};

export default Multiselectsearch;
